import { CategoryCycleError, categoryPath, slugify } from '@domain/finance'
import type { Category } from '@domain/finance'

/**
 * **A árvore de categorias como ela chega do banco: possivelmente quebrada.**
 *
 * A Cloud Function da Task 9 é quem cunha `path` e `depth`, numa transação, e
 * ela **recusa** quatro coisas: ciclo, `kind` divergente do pai, slug já
 * ocupado por uma irmã, e pai ainda não assentado. Quando ela recusa, a
 * transação aborta e **nada é escrito** — só que o documento do cliente já
 * está gravado. As decisões 105(b) e 105(g) descrevem o que sobra:
 *
 * - uma categoria **criada** e recusada fica **sem `path`**. Ela é invisível a
 *   toda consulta por ancestral (`array-contains`), então some dos totais por
 *   categoria sem sumir da tela;
 * - uma categoria **movida** e recusada fica com o `parentId` **novo** ao lado
 *   do `path` **velho** — inclusive um ciclo de verdade no grafo de `parentId`,
 *   se a recusa foi de ciclo;
 * - o `kind` do pai pode ser trocado depois, e a Function **não reconfere a
 *   descendência** (decisão 105f): uma filha `expense` fica pendurada num pai
 *   `income` com o `path` intacto, e nada a acusa.
 *
 * **Nada no tipo `Category` carrega o motivo da recusa**, e nada pode: o
 * documento não tem campo de erro, e a Function que recusou não escreveu nada.
 * Então o motivo tem de ser **reconstruído da forma da árvore**, que é o que
 * este módulo faz — e é a única coisa que fecha o buraco pelo lado da tela.
 *
 * ## Duas obrigações, e as duas são de vida ou morte
 *
 * 1. **Terminar.** Um ciclo em `parentId` — `X` filho de `Y` e `Y` filho de
 *    `X`, ou `X` filho de si mesma — trava qualquer descida ingênua, e travar
 *    é a tela em branco com a aba do navegador comendo CPU. A descida daqui
 *    carrega um conjunto de visitados, pelo mesmo motivo que
 *    `rewritePathsOnMove` carrega o dele.
 * 2. **Não esconder ninguém.** Categoria presa num ciclo, ou órfã de um pai
 *    apagado, **não é alcançável a partir de raiz nenhuma**. Uma tela que só
 *    desenhasse as raízes e descesse simplesmente não a mostraria — e o pior
 *    estado do sistema seria também o mais invisível. Daí `detached`: tudo que
 *    a descida não alcançou sai numa lista à parte, nomeada.
 */

/**
 * O que há de errado com uma categoria. **Código, e não frase**: a tela decide
 * como dizer (`DEFECT_LABELS`), e o teste compara sem depender da redação.
 */
export type CategoryDefect =
  /** A subida por `parentId` reencontra alguém — há ciclo no grafo. */
  | 'ciclo'
  /** Um ancestral (o pai, ou mais acima) não está na lista. Pai apagado. */
  | 'ancestral-ausente'
  /** `path` vazio: a Function ainda não passou, ou passou e recusou. */
  | 'sem-caminho'
  /** O `path` gravado não é o que a árvore de hoje produz. Movimento recusado. */
  | 'caminho-desatualizado'
  /** `depth` não é `path.length - 1`. */
  | 'profundidade-errada'
  /** `kind` herdado do pai não pode divergir, e este diverge. */
  | 'kind-diverge-do-pai'
  /** Duas irmãs com o mesmo slug têm o mesmo `path`. */
  | 'slug-repetido-entre-irmas'
  /** Sem `path` e com um nome que não produz slug (`'☕'`): não há o que cunhar. */
  | 'nome-sem-slug'

export type CategoryNode = {
  category: Category
  defects: CategoryDefect[]
  children: CategoryNode[]
}

export type CategoryTreeView = {
  /** O que se alcança descendo a partir de `parentId: null`. */
  roots: CategoryNode[]
  /**
   * O que a descida **não** alcançou: órfãs de pai apagado e as presas em
   * ciclo. Lista à parte de propósito — ver a obrigação 2 no cabeçalho.
   */
  detached: CategoryNode[]
}

/** Em português, para a tela. Cada uma diz o que houve **e** o que fazer. */
export const DEFECT_LABELS: Record<CategoryDefect, string> = {
  ciclo: 'ciclo em parentId — esta categoria é ancestral de si mesma; mova-a para outro pai',
  'ancestral-ausente': 'o pai não existe mais — escolha um pai que exista, ou leve para a raiz',
  'sem-caminho': 'sem path: a função de caminho não passou por ela, ou recusou. Some das somas por categoria',
  'caminho-desatualizado': 'o path gravado não bate com a árvore de hoje — o movimento foi recusado',
  'profundidade-errada': 'depth não é path.length - 1',
  'kind-diverge-do-pai': 'kind diferente do pai — kind é herdado e não pode divergir',
  'slug-repetido-entre-irmas': 'duas irmãs com o mesmo slug têm o mesmo path, e as somas por ancestral juntam as duas',
  'nome-sem-slug': 'o nome não produz slug (precisa de ao menos uma letra ou dígito)',
}

function byId(all: Category[]): Map<string, Category> {
  return new Map(all.map((category) => [category.id, category]))
}

/**
 * A subida por `parentId`, para responder o que `categoryPath` se recusa a
 * responder.
 *
 * `categoryPath` lança nos três casos — e faz certo, porque quem a chama está
 * calculando um caminho para **gravar**, e caminho de árvore corrompida não
 * existe. Só que a tela não está gravando: ela precisa **desenhar** a árvore
 * corrompida, e para isso precisa saber qual dos casos é. As duas causas têm
 * consertos diferentes (mover, versus escolher outro pai), então virar as duas
 * num "deu erro" seria jogar fora exatamente a informação que a pessoa precisa.
 */
function ancestryOf(
  category: Category,
  index: Map<string, Category>,
): 'ok' | 'ciclo' | 'ancestral-ausente' {
  const visited = new Set<string>([category.id])
  let currentId = category.parentId

  while (currentId !== null) {
    if (visited.has(currentId)) return 'ciclo'
    visited.add(currentId)
    const parent = index.get(currentId)
    if (parent === undefined) return 'ancestral-ausente'
    currentId = parent.parentId
  }
  return 'ok'
}

/**
 * O slug estável, ou `null` quando não há como saber qual é.
 *
 * Mesma regra de `ownSlug` no domínio: sai do `path` gravado, e `slugify(name)`
 * só entra quando não há `path` — que é o instante da criação. A diferença é
 * que aqui um nome sem slug **não lança**: ele é um defeito a mostrar, e uma
 * exceção derrubaria a tela inteira por causa de uma linha.
 */
function ownSlugOrNull(category: Category): string | null {
  const fromPath = category.path.at(-1)
  if (fromPath !== undefined) return fromPath
  try {
    return slugify(category.name)
  } catch {
    return null
  }
}

/** Os ids das categorias que dividem slug com uma irmã sob o mesmo pai. */
function collidingIds(all: Category[]): Set<string> {
  const byParentAndSlug = new Map<string, Category[]>()
  for (const category of all) {
    const slug = ownSlugOrNull(category)
    if (slug === null) continue
    // A chave é o par (pai, slug) serializado, e não uma concatenação com um
    // separador escolhido: `parentId` é um id do Firestore e `slug` sai de
    // `slugify`, então nenhum dos dois contém aspas ou colchetes — mas o par
    // serializado dispensa esse raciocínio, que é o que se quer numa chave.
    const key = JSON.stringify([category.parentId, slug])
    const group = byParentAndSlug.get(key)
    if (group === undefined) byParentAndSlug.set(key, [category])
    else group.push(category)
  }

  const colliding = new Set<string>()
  for (const group of byParentAndSlug.values()) {
    if (group.length < 2) continue
    for (const category of group) colliding.add(category.id)
  }
  return colliding
}

function samePath(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((segment, index) => segment === b[index])
}

function defectsOf(
  category: Category,
  all: Category[],
  index: Map<string, Category>,
  colliding: Set<string>,
): CategoryDefect[] {
  const defects: CategoryDefect[] = []

  const ancestry = ancestryOf(category, index)
  if (ancestry !== 'ok') defects.push(ancestry)

  const parent = category.parentId === null ? undefined : index.get(category.parentId)
  if (parent !== undefined && parent.kind !== category.kind) defects.push('kind-diverge-do-pai')

  if (colliding.has(category.id)) defects.push('slug-repetido-entre-irmas')

  if (category.path.length === 0) {
    defects.push('sem-caminho')
    if (ownSlugOrNull(category) === null) defects.push('nome-sem-slug')
    return defects
  }

  if (category.depth !== category.path.length - 1) defects.push('profundidade-errada')

  // Com a subida quebrada não há caminho esperado a comparar, e `categoryPath`
  // lançaria: o defeito de cima já é o que há para dizer.
  if (ancestry !== 'ok') return defects

  try {
    if (!samePath(categoryPath(category, all), category.path)) {
      defects.push('caminho-desatualizado')
    }
  } catch (error) {
    // A subida disse `ok`, então sobra o nome sem slug de um ancestral sem
    // `path` — e é isso que a decisão 105(a) descreve. Não engolir: sem esta
    // linha o defeito viraria silêncio, que é o que este módulo existe para
    // acabar.
    defects.push(error instanceof CategoryCycleError ? 'ciclo' : 'nome-sem-slug')
  }

  return defects
}

/** Ordem estável e legível. `path` não serve como chave: metade da árvore
 * quebrada não tem um. */
function byName(a: Category, b: Category): number {
  return a.name.localeCompare(b.name, 'pt-BR') || a.id.localeCompare(b.id)
}

export function categoryTreeView(all: Category[]): CategoryTreeView {
  const index = byId(all)
  const colliding = collidingIds(all)

  const childrenOf = new Map<string, Category[]>()
  const roots: Category[] = []
  for (const category of all) {
    if (category.parentId === null) {
      roots.push(category)
      continue
    }
    const siblings = childrenOf.get(category.parentId)
    if (siblings === undefined) childrenOf.set(category.parentId, [category])
    else siblings.push(category)
  }

  // O conjunto de visitados é o que faz a descida terminar sobre um ciclo — e
  // é também o que separa `roots` de `detached` no fim, sem uma segunda
  // varredura que pudesse discordar desta.
  const visited = new Set<string>()

  function build(category: Category): CategoryNode {
    visited.add(category.id)
    const children: CategoryNode[] = []
    for (const child of [...(childrenOf.get(category.id) ?? [])].sort(byName)) {
      if (visited.has(child.id)) continue
      children.push(build(child))
    }
    return { category, defects: defectsOf(category, all, index, colliding), children }
  }

  const builtRoots = [...roots].sort(byName).map(build)

  // Quem sobrou não é alcançável a partir de raiz nenhuma: órfã de pai apagado,
  // ou presa num ciclo. Cada uma vira uma sub-árvore própria, com a mesma
  // guarda de visitados — uma categoria filha de si mesma entra aqui e para na
  // primeira volta.
  //
  // **O laço é imperativo de propósito.** `filter(...).map(build)` percorreria
  // a lista inteira ANTES de a primeira chamada de `build` marcar quem quer que
  // seja, então cada membro de um ciclo de três viraria uma sub-árvore própria
  // e a mesma categoria apareceria três vezes na tela. Aqui o `visited` que o
  // `build` anterior atualizou é o que a volta seguinte lê.
  const detached: CategoryNode[] = []
  for (const category of [...all].sort(byName)) {
    if (visited.has(category.id)) continue
    detached.push(build(category))
  }

  return { roots: builtRoots, detached }
}

/** Achatada, na ordem em que a tela desenha: cada nó com o próprio nível. */
export function flattenTree(nodes: CategoryNode[], level = 0): { node: CategoryNode; level: number }[] {
  return nodes.flatMap((node) => [
    { node, level },
    ...flattenTree(node.children, level + 1),
  ])
}
